

import React, { createContext, ReactNode, useState, useEffect } from 'react';

import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';

import { AuthContextType } from './types/authContextType';
import { useCookies } from 'react-cookie';
import ECookies from '../config/cookies';
import axios from 'axios';
import { AUTH_ENDPOINTS } from '../config/apiEndpoints';
import { IEmployer } from '../data_models/employer';
import { EUserType } from '../data_models/enums';
import { IFreelancer } from '../data_models/freelancer';
import { useSocket } from '../hooks/useSocket';



const firebaseConfigDev = {
    apiKey: "AIzaSyAnLTLIdhKGFOF0-vBhP-r7YSpx9pNBqBM",
    authDomain: "gamers-home-dev.firebaseapp.com",
    projectId: "gamers-home-dev",
    storageBucket: "gamers-home-dev.firebasestorage.app",
    messagingSenderId: "801346074361",
    appId: "1:801346074361:web:7e96d74ea7911fc2951428"
};
const firebaseConfig = window.env?.FIREBASE_CONFIG
    ? JSON.parse(window.env.FIREBASE_CONFIG)
    : firebaseConfigDev;

// initialize firebase
const firebaseApp = initializeApp(firebaseConfig);
const auth = getAuth(firebaseApp);

// Create the context
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {

    const { disconnectSocket } = useSocket();

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const [cookies, setCookie, removeCookie] = useCookies([
        ECookies.AUTH_TOKEN_COOKIE,
        ECookies.CURRENT_USER_COOKIE,
        ECookies.CURRENT_USER_TYPE_COOKIE,
        ECookies.TECH_AUTH_USER_COOKIE,
    ]);
    const [authToken, setAuthToken] = useState<string | undefined>(cookies[ECookies.AUTH_TOKEN_COOKIE]);

    useEffect(() => {
        try {
            const token = cookies[ECookies.AUTH_TOKEN_COOKIE];
            if (token) {
                setAuthToken(token);
            }
        } catch (error) {
            console.error("AuthProvider error:", error)
        }

    }, []);

    const handleApiError = (error: any) => {
        if (axios.isAxiosError(error) && error.response) {
            setError(error.response.data.error || 'An error occurred');
        } else if (error instanceof Error) {
            setError(error.message);
        } else {
            setError('An unexpected error occurred');
        }
    };

    const clearError = () => setError(null);

    async function signupEmployer(email: string, password: string): Promise<IEmployer> {
        setLoading(true);
        clearError();

        // wait 2 seconds
        await new Promise((resolve) => setTimeout(resolve, 2000));

        try {
            let lowerCaseEmail = email.toLowerCase();
            const response = await axios.post(AUTH_ENDPOINTS.POST_EMPLOYER_SIGNUP, {
                email: lowerCaseEmail,
                password,
            });

            const { user, token } = response.data;

            // clear up cookies just in case
            logout();

            setAuthToken(token);
            // set auth token cookie
            setCookie(ECookies.AUTH_TOKEN_COOKIE, token, {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });
            // set cookie for current user
            setCookie(ECookies.CURRENT_USER_TYPE_COOKIE, EUserType.EMPLOYER, {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });
            setCookie(ECookies.CURRENT_USER_COOKIE, JSON.stringify(user), {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });
            // technical cookie - notifies certain components that user is set
            setCookie(ECookies.TECH_AUTH_USER_COOKIE, new Date().getUTCMilliseconds(), {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });


            return user;
        } catch (error: any) {
            handleApiError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }

    async function signupFreelancer(email: string, password: string): Promise<IFreelancer> {
        setLoading(true);
        clearError();
        try {

            let lowerCaseEmail = email.toLowerCase();
            const response = await axios.post(AUTH_ENDPOINTS.POST_FREELANCER_SIGNUP, {
                email: lowerCaseEmail,
                password
            });

            const { user, token } = response.data;

            // clear up cookies just in case
            logout();

            setAuthToken(token);

            // set auth token cookie
            setCookie(ECookies.AUTH_TOKEN_COOKIE, token, {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });
            // set cookie for current user type
            setCookie(ECookies.CURRENT_USER_TYPE_COOKIE, EUserType.FREELANCER, {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });
            // set cookie for current user
            setCookie(ECookies.CURRENT_USER_COOKIE, JSON.stringify(user), {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });
            // technical cookie - notifies certain components that user is set
            setCookie(ECookies.TECH_AUTH_USER_COOKIE, new Date().getUTCMilliseconds(), {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });

            return user;
        } catch (e: any) {
            handleApiError(e);
            throw error;
        } finally {
            setLoading(false);
        }
    }

    async function loginFreelancer(email: string, password: string) {

        setLoading(true);
        clearError();
        // clear up cookies just in case
        logout();

        let lowerCaseEmail = email.toLowerCase();
        try {
            // get firebase user and id token
            let idToken;
            try {
                if (!auth) throw new Error('Firebase not initialized');

                const userCredential = await signInWithEmailAndPassword(auth, lowerCaseEmail, password);
                const firebaseUser = userCredential.user;
                idToken = await firebaseUser.getIdToken();
            } catch (error) {
                console.error(error);
                throw new Error('Invalid email or password');
            }
            if (!idToken) throw new Error('Cannot login');

            const response = await axios.post(AUTH_ENDPOINTS.POST_FREELANCER_LOGIN, {
                idToken,
                email: lowerCaseEmail,
            });

            const { user, token } = response.data;
            setAuthToken(token);

            // set auth token cookie
            setCookie(ECookies.AUTH_TOKEN_COOKIE, token, {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });

            // set cookie for current user type
            setCookie(ECookies.CURRENT_USER_TYPE_COOKIE, EUserType.FREELANCER, {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });

            // set cookie for current user
            setCookie(ECookies.CURRENT_USER_COOKIE, JSON.stringify(user), {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });

            // technical cookie - notifies certain components that user is set
            setCookie(ECookies.TECH_AUTH_USER_COOKIE, new Date().getUTCMilliseconds(), {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });

            return user;
        } catch (error: any) {
            handleApiError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }

    async function loginEmployer(email: string, password: string) {
        setLoading(true);
        clearError();
        // clear up cookies just in case
        logout();

        let lowerCaseEmail = email.toLowerCase();
        try {

            // get firebase user and id token
            let idToken;
            try {
                if (!auth) throw new Error('Firebase not initialized');

                const userCredential = await signInWithEmailAndPassword(auth, lowerCaseEmail, password);
                const firebaseUser = userCredential.user;
                idToken = await firebaseUser.getIdToken();
            } catch (error) {
                console.error(error);
                throw new Error('Invalid email or password');
            }
            if (!idToken) throw new Error('Cannot login');

            // send id token to backend
            const response = await axios.post(AUTH_ENDPOINTS.POST_EMPLOYER_LOGIN, {
                idToken,
                email: lowerCaseEmail,
            });

            const { user, token } = response.data;
            setAuthToken(token);

            // set auth token cookie
            setCookie(ECookies.AUTH_TOKEN_COOKIE, token, {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });

            // set cookie for current user type
            setCookie(ECookies.CURRENT_USER_TYPE_COOKIE, EUserType.EMPLOYER, {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });

            // set cookie for current user
            setCookie(ECookies.CURRENT_USER_COOKIE, JSON.stringify(user), {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });

            // technical cookie - notifies certain components that user is set
            setCookie(ECookies.TECH_AUTH_USER_COOKIE, new Date().getUTCMilliseconds(), {
                path: '/',
                secure: true,
                sameSite: 'strict',
            });

            return user;
        } catch (error: any) {
            handleApiError(error);
            throw error;
        } finally {
            setLoading(false);
        }


    }

    async function sendForgotPasswordEmail(email: string) {
        setLoading(true);
        clearError();
        try {
            await axios.post(AUTH_ENDPOINTS.POST_SEND_FORGOT_PASSWORD_EMAIL, {
                email,
            });
        } catch (error: any) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    }

    async function changePasswordByToken(token: string, newPassword: string) {
        setLoading(true);
        clearError();
        try {
            await axios.post(AUTH_ENDPOINTS.POST_CHANGE_PASSWORD, {
                token,
                newPassword,
            });
        } catch (error: any) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    }

    async function sendVerificationEmail() {
        setLoading(true);
        clearError();
        try {
            await axios.post(
                AUTH_ENDPOINTS.POST_SEND_VERIFICATION_EMAIL,
                {},
                {
                    headers: {
                        Authorization: authToken,
                    },
                }
            );
        } catch (error: any) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    }

    async function verifyEmail(token: string) {
        setLoading(true);
        clearError();

        try {
            await axios.post(AUTH_ENDPOINTS.POST_VERIFY_EMAIL, {
                token,
            });
        } catch (error: any) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    }


    async function logout() {
        // Clear the token from memory
        setAuthToken(undefined);

        // Remove user cookies
        removeCookie(ECookies.AUTH_TOKEN_COOKIE, { path: '/' });
        // removeCookie(ECookies.CURRENT_USER_TYPE_COOKIE, { path: '/' });
        removeCookie(ECookies.CURRENT_USER_COOKIE, { path: '/' });
        removeCookie(ECookies.TECH_AUTH_USER_COOKIE, { path: '/' });

        // Disconnect socket connection
        disconnectSocket();
    }

    // Context value
    const value: AuthContextType = {
        error,
        loading,
        authToken,
        signupEmployer,
        signupFreelancer,
        loginFreelancer,
        loginEmployer,
        sendVerificationEmail,
        verifyEmail,
        changePasswordByToken,
        sendForgotPasswordEmail,
        logout,
    };
    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

